import type { OrderListItem } from '@agos/api-client/model'
import { createContext, useContext, type ComponentType, type ReactNode } from 'react'

/**
 * The till's *Zaplatiť*, for a screen in another module - `FRONTEND.md` §9.740, owner O111.
 *
 * **Why a slot rather than an import.** *Rozvoz - nástenka* lives in `@agos/manager-delivery` and the dialog the
 * owner asked it to open - *"the same dialog as Otvorené stoly"* - is `PaymentDialog` in `@agos/manager-orders`.
 * §5 forbids a module importing another, and a copy of a 1 500-line money dialog would be two dialogs that drift
 * apart on the first fix. Composing modules is the shell's job, so the shell fills this slot from the orders
 * module's public entry and the board asks for whatever it was given.
 *
 * **It fails closed, onto the board's own close.** Outside a provider the hook answers null, and the board then
 * closes the order as it did before §9.740 - a screen whose slot was never filled must not lose its *Vydaná*.
 */
export interface CounterPaymentProps {
  /** The row as `GET /orders` answers it, which is what the till's dialog reads. */
  order: OrderListItem
  onClose: () => void
  /** The order was paid or closed - the screen refreshes. */
  onPaid: () => void
}

export type CounterPaymentDialog = ComponentType<CounterPaymentProps>

const CounterPaymentContext = createContext<CounterPaymentDialog | null>(null)

export function CounterPaymentProvider({
  dialog,
  children,
}: {
  dialog: CounterPaymentDialog
  children: ReactNode
}) {
  return <CounterPaymentContext.Provider value={dialog}>{children}</CounterPaymentContext.Provider>
}

/** The dialog the shell provided, or null when none was - the caller keeps its own path then. */
export function useCounterPayment(): CounterPaymentDialog | null {
  return useContext(CounterPaymentContext)
}
